import { defineStore } from 'pinia'
import axios from 'axios'

const API_URL = '/api'
const AUTH_API = '/api/v1/auth'
const REFRESH_RETRY_MS = 15000

let refreshInFlight = null
let initializeInFlight = null
let refreshTimer = null
let interceptorsInstalled = false
let visibilityInstalled = false

function initialToken(name) {
  const stored = sessionStorage.getItem(name)
  if (stored) return stored
  if (name === 'packliste_access_token') {
    const legacy = localStorage.getItem('token')
    if (legacy) {
      sessionStorage.setItem(name, legacy)
      localStorage.removeItem('token')
      return legacy
    }
  }
  return null
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: initialToken('packliste_access_token'),
    refreshToken: initialToken('packliste_refresh_token'),
    expiresAt: Number(sessionStorage.getItem('packliste_expires_at') || 0),
    provider: sessionStorage.getItem('packliste_auth_provider') || 'native',
    user: null,
    authConfig: null,
    initialized: false,
    sessionGeneration: 0
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    nativeLoginEnabled: (state) => state.authConfig?.native_login_enabled ?? true,
    oidcLoginEnabled: (state) => state.authConfig?.oidc_login_enabled ?? false,
    oidcAutoRedirect: (state) => state.authConfig?.oidc_auto_redirect ?? false,
    registrationEnabled: (state) => state.authConfig?.user_registration_enabled ?? false,
    setupRequired: (state) => state.authConfig?.setup_required ?? false
  },

  actions: {
    async fetchAuthConfig() {
      const response = await axios.get(`${AUTH_API}/config`, { _skipAuthRefresh: true })
      this.authConfig = response.data
      return response.data
    },

    setSession(payload) {
      this.sessionGeneration += 1
      this.token = payload.access_token
      this.refreshToken = payload.refresh_token || null
      this.provider = payload.provider || 'native'
      this.expiresAt = Date.now() + Number(payload.expires_in || 0) * 1000

      sessionStorage.setItem('packliste_access_token', this.token)
      sessionStorage.setItem('packliste_expires_at', String(this.expiresAt))
      sessionStorage.setItem('packliste_auth_provider', this.provider)
      if (this.refreshToken) {
        sessionStorage.setItem('packliste_refresh_token', this.refreshToken)
      } else {
        sessionStorage.removeItem('packliste_refresh_token')
      }
      axios.defaults.headers.common.Authorization = `Bearer ${this.token}`
      this.scheduleRefresh()
    },

    clearSession() {
      this.sessionGeneration += 1
      this.token = null
      this.refreshToken = null
      this.expiresAt = 0
      this.provider = 'native'
      this.user = null
      sessionStorage.removeItem('packliste_access_token')
      sessionStorage.removeItem('packliste_refresh_token')
      sessionStorage.removeItem('packliste_expires_at')
      sessionStorage.removeItem('packliste_auth_provider')
      localStorage.removeItem('token')
      delete axios.defaults.headers.common.Authorization
      if (refreshTimer) clearTimeout(refreshTimer)
      refreshTimer = null
    },

    async login(credentials) {
      try {
        const response = await axios.post(`${AUTH_API}/login`, credentials, { _skipAuthRefresh: true })
        this.setSession(response.data)
        await this.fetchUser()
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.detail || 'Login failed'
        }
      }
    },

    async register(userData) {
      try {
        const response = await axios.post(`${API_URL}/register`, userData, { _skipAuthRefresh: true })
        return { success: true, user: response.data }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.detail || 'Registration failed'
        }
      }
    },

    async changePassword(passwordData) {
      try {
        await axios.put(`${API_URL}/me/password`, passwordData)
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.detail || 'Password change failed'
        }
      }
    },

    async fetchUser() {
      if (!this.token) return false
      const response = await axios.get(`${AUTH_API}/me`)
      this.user = response.data
      return true
    },

    async refreshSession() {
      if (!this.refreshToken) return false
      if (refreshInFlight) return refreshInFlight

      const generation = this.sessionGeneration
      const refreshToken = this.refreshToken
      refreshInFlight = (async () => {
        try {
          const response = await axios.post(
            `${AUTH_API}/refresh`,
            { refresh_token: refreshToken },
            { _skipAuthRefresh: true }
          )
          if (generation !== this.sessionGeneration) return false
          this.setSession(response.data)
          return true
        } catch (error) {
          if (error.response?.status === 401 && generation === this.sessionGeneration) {
            this.clearSession()
          } else if (generation === this.sessionGeneration) {
            this.scheduleRefresh(REFRESH_RETRY_MS)
          }
          throw error
        } finally {
          refreshInFlight = null
        }
      })()
      return refreshInFlight
    },

    scheduleRefresh(delayOverride = null) {
      if (refreshTimer) clearTimeout(refreshTimer)
      refreshTimer = null
      if (!this.refreshToken || !this.expiresAt) return

      const remaining = this.expiresAt - Date.now()
      const margin = Math.min(60000, Math.max(1000, remaining * 0.2))
      const delay = delayOverride ?? Math.max(1000, remaining - margin)
      refreshTimer = setTimeout(() => {
        this.refreshSession().catch(() => {})
      }, delay)
    },

    async startOidcLogin() {
      const redirectUri = `${window.location.origin}/auth/callback`
      const response = await axios.get(`${AUTH_API}/oidc/begin`, {
        params: { redirect_uri: redirectUri },
        _skipAuthRefresh: true
      })
      sessionStorage.setItem('packliste_oidc_state', response.data.state)
      sessionStorage.setItem('packliste_oidc_verifier', response.data.code_verifier)
      sessionStorage.setItem('packliste_oidc_redirect_uri', response.data.redirect_uri)
      window.location.assign(response.data.auth_url)
    },

    async completeOidcLogin({ code, state }) {
      const expectedState = sessionStorage.getItem('packliste_oidc_state')
      const codeVerifier = sessionStorage.getItem('packliste_oidc_verifier')
      const redirectUri = sessionStorage.getItem('packliste_oidc_redirect_uri')
      sessionStorage.removeItem('packliste_oidc_state')
      sessionStorage.removeItem('packliste_oidc_verifier')
      sessionStorage.removeItem('packliste_oidc_redirect_uri')

      if (!state || state !== expectedState) throw new Error('OIDC state parameter mismatch')
      if (!code || !codeVerifier || !redirectUri) throw new Error('OIDC login session is incomplete')

      const response = await axios.post(
        `${AUTH_API}/oidc/callback`,
        { code, state, code_verifier: codeVerifier, redirect_uri: redirectUri },
        { _skipAuthRefresh: true }
      )
      this.setSession(response.data)
      await this.fetchUser()
    },

    async logout() {
      const oidc = this.provider === 'oidc'
      let logoutUrl = null
      try {
        if (this.token) await axios.post(`${AUTH_API}/logout`, {})
        if (oidc) {
          const response = await axios.get(`${AUTH_API}/oidc/logout-url`, {
            params: { redirect_uri: `${window.location.origin}/login` },
            _skipAuthRefresh: true
          })
          logoutUrl = response.data.logout_url
        }
      } catch (error) {
        console.warn('Could not complete server-side logout:', error)
      } finally {
        this.clearSession()
      }
      window.location.href = logoutUrl || '/login'
    },

    async initializeAuth() {
      if (this.initialized) return
      if (initializeInFlight) return initializeInFlight
      initializeInFlight = (async () => {
        try {
          await this.fetchAuthConfig()
          if (this.token) {
            let renewalTemporarilyUnavailable = false
            axios.defaults.headers.common.Authorization = `Bearer ${this.token}`
            if (this.refreshToken && this.expiresAt - Date.now() < 60000) {
              try {
                await this.refreshSession()
              } catch (error) {
                if (error.response?.status !== 401) {
                  renewalTemporarilyUnavailable = true
                  console.warn('Session renewal is temporarily unavailable')
                }
              }
            }
            if (this.token && !renewalTemporarilyUnavailable) {
              try {
                await this.fetchUser()
                this.scheduleRefresh()
              } catch (error) {
                if (error.response?.status === 401) this.clearSession()
              }
            }
          }
        } finally {
          this.initialized = true
          initializeInFlight = null
        }
      })()
      return initializeInFlight
    },

    installInterceptors(router) {
      if (!interceptorsInstalled) {
        interceptorsInstalled = true
        axios.interceptors.request.use((config) => {
          if (this.token && !config.headers?.Authorization) {
            config.headers = config.headers || {}
            config.headers.Authorization = `Bearer ${this.token}`
          }
          return config
        })
        axios.interceptors.response.use(
          response => response,
          async (error) => {
            const request = error.config || {}
            const url = String(request.url || '')
            const excluded = request._skipAuthRefresh ||
              url.includes('/auth/login') ||
              url.includes('/auth/refresh') ||
              url.includes('/auth/oidc/') ||
              url.includes('/auth/keycloak/') ||
              url.includes('/auth/config') ||
              url.includes('/api/setup') ||
              url.includes('/api/public/')
            if (error.response?.status === 401 && !excluded && !request._packlisteRetry && this.refreshToken) {
              request._packlisteRetry = true
              try {
                const renewed = await this.refreshSession()
                if (renewed && this.token) {
                  request.headers = request.headers || {}
                  request.headers.Authorization = `Bearer ${this.token}`
                  return axios(request)
                }
              } catch (refreshError) {
                if (refreshError.response?.status !== 401) return Promise.reject(refreshError)
              }
            }
            if (error.response?.status === 401 && !excluded) {
              this.clearSession()
              if (router.currentRoute.value.path !== '/login') router.push('/login')
            }
            return Promise.reject(error)
          }
        )
      }

      if (!visibilityInstalled) {
        visibilityInstalled = true
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible' && this.refreshToken && this.expiresAt - Date.now() < 60000) {
            this.refreshSession().catch(() => {})
          }
        })
      }
    }
  }
})
